import Image from '@/components/Image'
import Username from '@/components/Username'
import { cn } from '@/lib/utils'
import type { Event } from '@nostr/tools/wasm'
import { useTranslation } from 'react-i18next'
import { badgeDisplayName, parseBadgeDefinition, pickBadgeImage } from './badge-utils'

/** NIP-58 kind 30009 badge definition. */
export default function BadgeDefinition({
  event,
  className
}: {
  event: Event
  className?: string
}) {
  const { t } = useTranslation()
  const meta = parseBadgeDefinition(event)
  const name = badgeDisplayName(meta)
  const image = pickBadgeImage(meta, true)

  return (
    <div className={cn('space-y-3', className)}>
      <div className="text-sm font-medium text-muted-foreground">{t('Badge')}</div>
      <div className="flex gap-4">
        {image && (
          <Image
            imageUrl={image}
            imagePubkey={event.pubkey}
            alt={name}
            classNames={{
              wrapper: 'aspect-square w-32 shrink-0 rounded-xl border bg-muted sm:w-40'
            }}
          />
        )}
        <div className="min-w-0 flex-1 space-y-1">
          {name && <h2 className="truncate text-xl font-semibold">{name}</h2>}
          {meta.d && (
            <div className="truncate font-mono text-xs text-muted-foreground">#d: {meta.d}</div>
          )}
          {meta.description && (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{meta.description}</p>
          )}
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <span>{t('Issued by')}</span>
            <Username user={event.pubkey} className="font-semibold" />
          </div>
        </div>
      </div>
      {event.content && <p className="whitespace-pre-wrap break-words text-sm">{event.content}</p>}
    </div>
  )
}
